import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { adsApi } from '@/api/client';
import { cn } from '@/utils/cn';
import { filterRowsByTableSearch } from '@/utils/tableSearch';
import {
  defaultNegativeMatchType,
  formatNegativeMatchType,
  isCampaignLevelNegative,
  negativeKeywordStateLabel,
  parseNegativeKeywordMutationResults,
  parseNegativeKeywordsPayload,
  type NegativeKeywordRow,
  type SbNegativeMatchType,
  type SpNegativeMatchType,
} from '@/api/negativeKeywords';
import { TableSearchInput } from '@/components/TableSearchInput';
import { useToast } from '@/components/ui/Toast';
import type { CampaignAdProduct } from '@/types/spCampaigns';

type AdGroupOption = { id: string; name: string };

type ScopeAdGroup = { ad_group_id?: string; name?: string; state?: string };

/** Amazon rejects negatives on archived ad groups, so only offer active or paused ones. */
export function negativeScopeAdGroupOptions(adGroups: ScopeAdGroup[]): AdGroupOption[] {
  return adGroups
    .filter((row) => row.ad_group_id && String(row.state || '').toUpperCase() !== 'ARCHIVED')
    .map((row) => {
      const id = String(row.ad_group_id);
      return { id, name: row.name || id };
    });
}

type CampaignDetailsNegativeKeywordsProps = {
  profileId: string;
  campaignId: string;
  adProduct: CampaignAdProduct;
  adGroups: ScopeAdGroup[];
  adGroupsLoading?: boolean;
  adGroupNameById?: Map<string, string>;
};

function mutationFailureMessage(
  results: ReturnType<typeof parseNegativeKeywordMutationResults>,
  fallback: string
): string | null {
  const failed = results.find((item) => {
    const status = String(item.status || '').toLowerCase();
    return status !== '' && status !== 'success';
  });
  if (!failed) return null;
  return failed.message || fallback;
}

export function CampaignDetailsNegativeKeywords({
  profileId,
  campaignId,
  adProduct,
  adGroups,
  adGroupsLoading = false,
  adGroupNameById,
}: CampaignDetailsNegativeKeywordsProps) {
  const qc = useQueryClient();
  const { showToast } = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [keywordText, setKeywordText] = useState('');
  const [matchType, setMatchType] = useState<SpNegativeMatchType | SbNegativeMatchType>(
    defaultNegativeMatchType(adProduct)
  );
  const [adGroupId, setAdGroupId] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<NegativeKeywordRow | null>(null);

  const isKeywordCampaign = adProduct === 'SP' || adProduct === 'SB';

  const adGroupOptions = useMemo(() => negativeScopeAdGroupOptions(adGroups), [adGroups]);

  const {
    data: negativesData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['campaign-details-section-negative-keywords', profileId, campaignId, adProduct],
    queryFn: async () => {
      if (adProduct === 'SP') {
        const res = await adsApi.listSpNegativeKeywords(profileId, {
          campaign_ids: [campaignId],
          states: ['ENABLED', 'PAUSED'],
          max_results: 1000,
        });
        if (!res.success) throw new Error('Failed to load negative keywords');
        return res.data;
      }
      const res = await adsApi.listSbNegativeKeywords(profileId, {
        campaignId,
        stateFilter: 'enabled,archived',
        count: 500,
      });
      if (!res.success) throw new Error('Failed to load negative keywords');
      return res.data;
    },
    enabled: !!profileId && !!campaignId && isKeywordCampaign,
  });

  // Deleted ids are hidden right away; the list reload that confirms it can take several seconds.
  const [removedIds, setRemovedIds] = useState<ReadonlySet<string>>(() => new Set());
  const payload = useMemo(() => {
    const parsed = parseNegativeKeywordsPayload(negativesData);
    const kept = parsed.negative_keywords.filter(
      (row) => !row.negative_keyword_id || !removedIds.has(row.negative_keyword_id)
    );
    const hidden = parsed.negative_keywords.length - kept.length;
    return hidden === 0
      ? parsed
      : { ...parsed, negative_keywords: kept, total_results: parsed.total_results - hidden };
  }, [negativesData, removedIds]);

  const visibleRows = useMemo(() => {
    return filterRowsByTableSearch(payload.negative_keywords, searchQuery, (row) => {
      const agId = row.ad_group_id || '';
      const agName = (agId && adGroupNameById?.get(agId)) || agId || '-';
      return [
        row.keyword_text,
        agName,
        formatNegativeMatchType(row.match_type || ''),
        negativeKeywordStateLabel(row.state, adProduct),
        row.negative_keyword_id,
      ];
    });
  }, [payload.negative_keywords, searchQuery, adGroupNameById, adProduct]);

  const invalidateLists = () => {
    void qc.invalidateQueries({
      queryKey: ['campaign-details-section-negative-keywords', profileId, campaignId],
    });
    void qc.invalidateQueries({
      queryKey: ['campaign-negative-keywords', profileId, campaignId],
    });
  };

  const createMutation = useMutation({
    mutationFn: async () => {
      const trimmed = keywordText.trim();
      if (!trimmed) throw new Error('Enter a negative keyword');
      let res;
      if (adProduct === 'SP') {
        const item = {
          campaign_id: campaignId,
          keyword_text: trimmed,
          match_type: matchType as SpNegativeMatchType,
          state: 'ENABLED' as const,
        };
        // Empty ad group = campaign-level negative.
        res = adGroupId
          ? await adsApi.createSpNegativeKeywords(profileId, {
              negative_keywords: [{ ...item, ad_group_id: adGroupId }],
            })
          : await adsApi.createSpCampaignNegativeKeywords(profileId, {
              negative_keywords: [item],
            });
      } else {
        if (!adGroupId) throw new Error('Select an ad group');
        res = await adsApi.createSbNegativeKeywords(profileId, {
          negative_keywords: [
            {
              campaign_id: campaignId,
              ad_group_id: adGroupId,
              keyword_text: trimmed,
              match_type: matchType as SbNegativeMatchType,
            },
          ],
        });
      }
      if (!res.success) throw new Error(res.message || 'Failed to add negative keyword');
      const failure = mutationFailureMessage(
        parseNegativeKeywordMutationResults(res.data),
        'Failed to add negative keyword'
      );
      if (failure) throw new Error(failure);
      return res;
    },
    onSuccess: () => {
      setAddOpen(false);
      setKeywordText('');
      setFormError(null);
      showToast('Negative keyword added', 'success');
      invalidateLists();
    },
    onError: (err: Error) => {
      setFormError(err.message || 'Failed to add negative keyword');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (row: NegativeKeywordRow) => {
      if (!row.negative_keyword_id) throw new Error('Missing negative keyword id');
      if (adProduct === 'SP') {
        // The backend routes ad-group vs campaign-level deletes by the stored product level.
        const res = await adsApi.deleteSpNegativeKeywords(profileId, {
          negative_keyword_ids: [row.negative_keyword_id],
        });
        if (!res.success) throw new Error(res.message || 'Failed to delete negative keyword');
        const failure = mutationFailureMessage(
          parseNegativeKeywordMutationResults(res.data),
          'Failed to delete negative keyword'
        );
        if (failure) throw new Error(failure);
        return res;
      }
      const res = await adsApi.deleteSbNegativeKeyword(profileId, row.negative_keyword_id);
      if (!res.success) throw new Error(res.message || 'Failed to delete negative keyword');
      return res;
    },
    onSuccess: (_res, row) => {
      const removedId = row.negative_keyword_id;
      if (removedId) setRemovedIds((prev) => new Set(prev).add(removedId));
      setPendingDelete(null);
      setActionError(null);
      showToast('Negative keyword removed', 'success');
      invalidateLists();
    },
    onError: (err: Error) => {
      setActionError(err.message || 'Failed to delete negative keyword');
      showToast(err.message || 'Failed to delete negative keyword', 'error');
    },
  });

  const openAdd = () => {
    setFormError(null);
    setMatchType(defaultNegativeMatchType(adProduct));
    setKeywordText('');
    if (adProduct === 'SP') {
      // Prefer campaign-level by default when adding from the campaign page.
      setAdGroupId('');
    } else if (adGroupOptions.length === 1) {
      setAdGroupId(adGroupOptions[0].id);
    } else {
      setAdGroupId('');
    }
    setAddOpen(true);
  };

  const closeAdd = () => {
    if (createMutation.isPending) return;
    setAddOpen(false);
    setFormError(null);
  };

  if (!isKeywordCampaign) {
    return (
      <div>
        <h2 className="text-lg font-semibold text-foreground mb-3">Negative keywords</h2>
        <p className="text-sm text-foreground-subtle">
          Negative keywords are available for Sponsored Products and Sponsored Brands campaigns.
        </p>
      </div>
    );
  }

  const addButton = (
    <button
      type="button"
      onClick={openAdd}
      className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 bg-brand text-brand-foreground rounded-lg hover:bg-brand-hover font-medium"
    >
      <Plus className="w-4 h-4" aria-hidden />
      Add negative keyword
    </button>
  );

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h2 className="text-lg font-semibold text-foreground">Negative keywords</h2>
        <div className="flex flex-wrap items-center gap-2">
          {!isLoading && !error && payload.negative_keywords.length > 0 ? (
            <>
              <TableSearchInput
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Search negative keywords…"
                id="campaign-details-section-negatives-search"
              />
              <span className="text-sm text-foreground-subtle">{payload.total_results} total</span>
            </>
          ) : null}
          {addButton}
        </div>
      </div>


      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-foreground-subtle">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading negative keywords...
        </div>
      ) : error ? (
        <div className="text-sm text-error">
          {(error as Error).message || 'Failed to load negative keywords'}
          <button
            type="button"
            onClick={() => void refetch()}
            className="ml-2 text-brand underline"
          >
            Retry
          </button>
        </div>
      ) : payload.negative_keywords.length === 0 ? (
        <p className="text-sm text-foreground-subtle">
          No negative keywords found for this campaign.
        </p>
      ) : visibleRows.length === 0 ? (
        <p className="text-sm text-foreground-subtle">No rows match your search.</p>
      ) : (
        <>
          {searchQuery.trim() ? (
            <p className="text-sm text-foreground-subtle mb-2">
              Showing {visibleRows.length} of {payload.negative_keywords.length} negative keyword(s)
            </p>
          ) : null}
          <div className="max-h-80 overflow-x-auto overflow-y-auto rounded-xl border border-border">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-page text-left text-xs font-medium text-foreground-muted uppercase tracking-wide">
                  <th className="px-3 py-2">Keyword</th>
                  <th className="px-3 py-2">Match type</th>
                  <th className="px-3 py-2">State</th>
                  <th className="px-3 py-2">Ad group</th>
                  <th className="px-3 py-2 text-right">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {visibleRows.map((row, index) => {
                  const agId = row.ad_group_id || '';
                  const agName = isCampaignLevelNegative(row)
                    ? 'Entire campaign'
                    : (agId && adGroupNameById?.get(agId)) || agId || '-';
                  const isDeleting =
                    deleteMutation.isPending &&
                    deleteMutation.variables?.negative_keyword_id === row.negative_keyword_id;
                  return (
                    <tr
                      key={row.negative_keyword_id || `neg-${index}`}
                      className="bg-surface hover:bg-page/80"
                    >
                      <td className="px-3 py-2 text-foreground">{row.keyword_text || '-'}</td>
                      <td className="px-3 py-2 text-foreground-muted">
                        {formatNegativeMatchType(row.match_type || '')}
                      </td>
                      <td className="px-3 py-2 text-foreground-muted">
                        {negativeKeywordStateLabel(row.state, adProduct)}
                      </td>
                      <td className="px-3 py-2 text-foreground-muted">{agName}</td>
                      <td className="px-3 py-2 text-right">
                        <button
                          type="button"
                          disabled={deleteMutation.isPending || createMutation.isPending}
                          onClick={() => {
                            setActionError(null);
                            setPendingDelete(row);
                          }}
                          className={cn(
                            'inline-flex h-7 w-7 items-center justify-center rounded-md text-foreground-muted',
                            'hover:bg-error-subtle hover:text-error-ink',
                            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/30',
                            'disabled:cursor-not-allowed disabled:opacity-50'
                          )}
                          aria-label={`Remove negative keyword ${row.keyword_text || ''}`}
                          title="Remove"
                        >
                          {isDeleting ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                          ) : (
                            <Trash2 className="h-3.5 w-3.5" aria-hidden />
                          )}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {addOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="add-negative-keyword-title"
        >
          <div className="w-full max-w-md rounded-xl border border-border bg-surface p-5 shadow-lg">
            <h3
              id="add-negative-keyword-title"
              className="mb-4 text-base font-semibold text-foreground"
            >
              Add negative keyword
            </h3>
            <div className="space-y-3">
              <div>
                <label
                  htmlFor="campaign-details-neg-keyword-text"
                  className="mb-1 block text-sm font-medium text-foreground-muted"
                >
                  Keyword
                </label>
                <input
                  id="campaign-details-neg-keyword-text"
                  type="text"
                  value={keywordText}
                  disabled={createMutation.isPending}
                  onChange={(event) => {
                    setKeywordText(event.target.value);
                    if (formError) setFormError(null);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      createMutation.mutate();
                    }
                    if (event.key === 'Escape') {
                      event.preventDefault();
                      closeAdd();
                    }
                  }}
                  className="w-full rounded-lg border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30"
                  placeholder="e.g. free sample"
                  autoFocus
                />
              </div>
              <div>
                <label
                  htmlFor="campaign-details-neg-match-type"
                  className="mb-1 block text-sm font-medium text-foreground-muted"
                >
                  Match type
                </label>
                <select
                  id="campaign-details-neg-match-type"
                  value={matchType}
                  disabled={createMutation.isPending}
                  onChange={(event) =>
                    setMatchType(event.target.value as SpNegativeMatchType | SbNegativeMatchType)
                  }
                  className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30"
                >
                  {adProduct === 'SP' ? (
                    <>
                      <option value="NEGATIVE_EXACT">Negative exact</option>
                      <option value="NEGATIVE_PHRASE">Negative phrase</option>
                    </>
                  ) : (
                    <>
                      <option value="negativeExact">Negative exact</option>
                      <option value="negativePhrase">Negative phrase</option>
                    </>
                  )}
                </select>
              </div>
              <div>
                <label
                  htmlFor="campaign-details-neg-ad-group"
                  className="mb-1 block text-sm font-medium text-foreground-muted"
                >
                  Scope
                </label>
                {adProduct === 'SB' && !adGroupsLoading && adGroupOptions.length === 0 ? (
                  <p className="text-sm text-foreground-subtle">
                    Create an ad group first, then add negative keywords.
                  </p>
                ) : (
                  <select
                    id="campaign-details-neg-ad-group"
                    value={adGroupId}
                    disabled={createMutation.isPending}
                    onChange={(event) => setAdGroupId(event.target.value)}
                    className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30"
                  >
                    {adProduct === 'SP' ? (
                      <option value="">Entire campaign</option>
                    ) : (
                      <option value="">Select ad group</option>
                    )}
                    {adGroupOptions.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.name}
                      </option>
                    ))}
                    {adGroupsLoading ? (
                      <option value="__loading" disabled>
                        Loading ad groups…
                      </option>
                    ) : null}
                  </select>
                )}
                {adProduct === 'SP' && !adGroupsLoading && adGroupOptions.length === 0 ? (
                  <p className="mt-1 text-xs text-foreground-subtle">
                    This campaign has no active ad groups, so the negative applies to the entire
                    campaign.
                  </p>
                ) : null}
              </div>
              {formError ? <p className="text-sm text-error">{formError}</p> : null}
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={closeAdd}
                disabled={createMutation.isPending}
                className="rounded-lg border border-border px-3 py-2 text-sm hover:bg-page disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={
                  createMutation.isPending ||
                  (adProduct === 'SB' && (adGroupsLoading || adGroupOptions.length === 0))
                }
                onClick={() => createMutation.mutate()}
                className="inline-flex items-center gap-1.5 rounded-lg bg-brand px-3 py-2 text-sm font-medium text-brand-foreground hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-50"
              >
                {createMutation.isPending ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                    Adding…
                  </>
                ) : (
                  'Add'
                )}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {pendingDelete ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-negative-keyword-title"
          onClick={deleteMutation.isPending ? undefined : () => setPendingDelete(null)}
        >
          <div
            className="w-full max-w-sm rounded-xl border border-border bg-surface p-5 shadow-lg"
            onClick={(event) => event.stopPropagation()}
          >
            <h3
              id="delete-negative-keyword-title"
              className="mb-2 text-base font-semibold text-foreground"
            >
              Remove negative keyword?
            </h3>
            <p className="mb-4 text-sm text-foreground-muted">
              <strong className="text-foreground">
                {pendingDelete.keyword_text || pendingDelete.negative_keyword_id}
              </strong>{' '}
              ({formatNegativeMatchType(pendingDelete.match_type || '')},{' '}
              {isCampaignLevelNegative(pendingDelete)
                ? 'entire campaign'
                : adGroupNameById?.get(pendingDelete.ad_group_id || '') ||
                  pendingDelete.ad_group_id}
              ) will stop blocking searches once removed. This can't be undone.
            </p>
            {actionError ? <p className="mb-3 text-sm text-error">{actionError}</p> : null}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setPendingDelete(null)}
                disabled={deleteMutation.isPending}
                className="rounded-lg border border-border px-3 py-2 text-sm hover:bg-page disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => deleteMutation.mutate(pendingDelete)}
                disabled={deleteMutation.isPending}
                className="inline-flex items-center gap-1.5 rounded-lg bg-error px-3 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
              >
                {deleteMutation.isPending ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                    Removing…
                  </>
                ) : (
                  'Remove'
                )}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
